import { useMemo } from "react";

import * as Sentry from "@sentry/react";

import type { RecordingError, RecordingErrorKind } from "./use-media-recorder";
import type { StreamError, StreamErrorKind } from "./use-media-stream";

const CATEGORY = "webcam-capture";

/** Which product surface a capture event came from. */
export type CaptureSurface = "socratic" | "device_check" | "work_attachment";

/**
 * Sentry reporting for the shared webcam-capture stack.
 */
export function webcamBreadcrumb(
  message: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({ category: CATEGORY, level: "info", message, data });
}

export function captureWebcamWarning(
  message: string,
  tags: Record<string, string | number | boolean>,
  extra: Record<string, unknown>
): void {
  Sentry.captureMessage(message, { level: "warning", tags, extra });
}

const HARDWARE_STREAM_KINDS: ReadonlySet<StreamErrorKind> = new Set([
  "not_found",
  "overconstrained",
  "unsupported",
  "unknown",
]);

const NON_ALERTABLE_RECORDING_ERROR_KINDS: ReadonlySet<RecordingErrorKind> =
  new Set(["size_exceeded", "duration_exceeded"]);

export interface UseWebcamCaptureTelemetryOptions {
  surface: CaptureSurface;
}

/**
 * Handlers for telemetry events from the webcam capture stack
 */
export function useWebcamCaptureTelemetry({
  surface,
}: UseWebcamCaptureTelemetryOptions): {
  onStreamError: (error: StreamError) => void;
  onRecordingError: (error: RecordingError) => void;
} {
  return useMemo(
    () => ({
      onStreamError(error) {
        webcamBreadcrumb("getUserMedia failed", {
          capture_surface: surface,
          stream_error_kind: error.kind,
        });
        if (HARDWARE_STREAM_KINDS.has(error.kind)) {
          captureWebcamWarning(
            "Web capture could not access device",
            { capture_surface: surface, stream_error_kind: error.kind },
            { message: error.message }
          );
        }
      },
      onRecordingError(error) {
        const tags = {
          capture_surface: surface,
          recording_error_kind: error.kind,
          recording_error_name: error.domErrorName ?? "none",
        };
        webcamBreadcrumb("recording failed", tags);
        if (!NON_ALERTABLE_RECORDING_ERROR_KINDS.has(error.kind)) {
          captureWebcamWarning("Web capture recording failed", tags, {
            message: error.message,
          });
        }
      },
    }),
    [surface]
  );
}
